/**
 * `/market` — RO-3-04 (task #44297), owned by the Customers section map.
 *
 * Port of `excellent/app/market/page.tsx` (212 LOC REWRITE) + its `_client.tsx`
 * (already in `client/shared/market/market-actions.tsx` via RO-3-00) and
 * `_icp-client.tsx` (ported as `client/shared/market/icp-section.tsx`).
 *
 * The page was a server component over marketOverview / listIcpProfiles /
 * listCompetitors / swotBoard / listOpenFindings / listRecentRuns / runningRun.
 * Those loaders now ride `market.page` (GET /api/market/page). Mutations stay on
 * the already-mounted market.* ops. Live Next original UNTOUCHED (D6).
 */

import {
  Card,
  StatStrip,
  type StatStripItem,
  IntentBadge,
  formatRelativeTime,
  PageHeader,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { market } from "@excellent/ui/api/contracts/market";
import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { RunAnalysisButton, FindingActions } from "../shared/market/market-actions";
import { IcpSection } from "../shared/market/icp-section";
import type { IcpProfileDTO } from "@excellent/ui/api/contracts/market-icp";

const THREAT_INTENT: Record<string, "danger" | "warning" | "neutral"> = {
  high: "danger",
  medium: "warning",
  low: "neutral",
};

const SWOT_TITLES = {
  strength: "Strengths",
  weakness: "Weaknesses",
  opportunity: "Opportunities",
  threat: "Threats",
} as const;

function MarketPage() {
  const { data, error, isLoading } = useApi(market.page, {});

  if (isLoading && !data) {
    return (
      <div className="space-y-8 px-6 py-8">
        <PageHeader title="Market" description="Loading market intelligence…" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="space-y-8 px-6 py-8">
        <PageHeader
          title="Market"
          description={error?.message ?? "Could not load market intelligence."}
        />
      </div>
    );
  }

  const ov = data.overview as {
    icpCount: number;
    competitorCount: number;
    highThreatCount: number;
    swotTotal: number;
    openFindings: number;
    lastRun?: { summary?: string | null } | null;
    primaryIcp?: { name: string; description?: string | null } | null;
  };
  const icps = data.icps as unknown as IcpProfileDTO[];
  const competitors = data.competitors as Array<{
    id: number;
    name: string;
    category: string;
    threat_level: string;
    tier?: string | null;
    positioning?: string | null;
    our_edge?: string | null;
  }>;
  const board = data.board as Record<
    "strength" | "weakness" | "opportunity" | "threat",
    Array<{ id: number; title: string; detail?: string | null }>
  >;
  const findings = data.findings as Array<{
    id: number;
    finding_kind: string;
    title: string;
    detail?: string | null;
    fix_prompt?: string | null;
  }>;
  const runs = data.runs as Array<{
    id: number;
    kind: string;
    status: string;
    started_at: string;
  }>;
  const running = data.running;

  const empty = ov.icpCount === 0 && ov.competitorCount === 0 && ov.swotTotal === 0;

  const stats: StatStripItem[] = [
    { title: "ICP segments", value: String(ov.icpCount) },
    { title: "Competitors", value: String(ov.competitorCount) },
    { title: "High threat", value: String(ov.highThreatCount) },
    { title: "SWOT items", value: String(ov.swotTotal) },
    { title: "Open findings", value: String(ov.openFindings) },
  ];

  return (
    <div className="space-y-8 px-6 py-8">
      <PageHeader
        title="Market"
        description="ICP, competitors, SWOT, and analysis recommendations."
      />

      {empty ? (
        <Card className="p-6">
          <h2 className="type-title font-medium text-foreground">Map your market</h2>
          <p className="mt-1 max-w-prose type-body text-muted-foreground">
            Nothing here yet. Run an analysis to discover your competitors,
            sharpen your ideal-customer profile, build a SWOT, and surface
            where to source leads. It threads through Sales and Marketing
            automatically.
          </p>
          <div className="mt-4">
            <RunAnalysisButton running={running} label="Map my market" />
          </div>
        </Card>
      ) : (
        <StatStrip items={stats} variant="standalone" />
      )}

      {ov.lastRun?.summary || ov.primaryIcp ? (
        <Card className="p-5">
          <div className="type-subtext font-medium text-muted-foreground">Positioning</div>
          {ov.lastRun?.summary ? (
            <p className="mt-1.5 type-body text-foreground">{ov.lastRun.summary}</p>
          ) : null}
          {ov.primaryIcp ? (
            <p className="mt-2 type-body text-muted-foreground">
              <span className="text-foreground">Primary ICP — {ov.primaryIcp.name}:</span>{" "}
              {ov.primaryIcp.description}
            </p>
          ) : null}
        </Card>
      ) : null}

      {ov.swotTotal > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(["strength", "weakness", "opportunity", "threat"] as const).map((k) => (
            <Card key={k} className="p-4">
              <div className="type-body font-medium text-foreground">{SWOT_TITLES[k]}</div>
              <ul className="mt-2 space-y-2">
                {board[k].length === 0 ? (
                  <li className="type-body text-muted-foreground">—</li>
                ) : (
                  board[k].map((s) => (
                    <li
                      key={s.id}
                      className="border-b border-border/40 pb-2 last:border-b-0 last:pb-0"
                    >
                      <div className="type-body text-foreground">{s.title}</div>
                      {s.detail ? (
                        <div className="type-subtext text-muted-foreground">{s.detail}</div>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </Card>
          ))}
        </div>
      ) : null}

      {competitors.length > 0 ? (
        <Card className="p-0">
          <div className="border-b border-border/60 px-6 py-3 type-body font-medium text-foreground">
            Competitors
          </div>
          <ul>
            {competitors.map((c) => (
              <li
                key={c.id}
                className="border-b border-border/40 px-6 py-3 last:border-b-0"
              >
                <div className="flex items-center gap-2">
                  <span className="type-body text-foreground">{c.name}</span>
                  <IntentBadge intent="neutral">{c.category}</IntentBadge>
                  <IntentBadge intent={THREAT_INTENT[c.threat_level] ?? "neutral"}>
                    {c.threat_level} threat
                  </IntentBadge>
                  {c.tier === "primary" ? (
                    <span className="type-subtext text-muted-foreground">· primary</span>
                  ) : null}
                </div>
                {c.positioning ? (
                  <p className="mt-1 type-body text-muted-foreground">{c.positioning}</p>
                ) : null}
                {c.our_edge ? (
                  <p className="mt-0.5 type-subtext text-muted-foreground">
                    <span className="text-foreground">Our edge:</span> {c.our_edge}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <IcpSection profiles={icps} />

      {findings.length > 0 ? (
        <Card className="p-0">
          <div className="border-b border-border/60 px-6 py-3 type-body font-medium text-foreground">
            Recommendations &amp; lead sources
          </div>
          <ul>
            {findings.map((f) => (
              <li
                key={f.id}
                className="flex items-start justify-between gap-4 border-b border-border/40 px-6 py-3 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <IntentBadge intent="neutral">{f.finding_kind}</IntentBadge>
                    <span className="type-body text-foreground">{f.title}</span>
                  </div>
                  {f.detail ? (
                    <p className="mt-1 type-subtext text-muted-foreground">{f.detail}</p>
                  ) : null}
                </div>
                <FindingActions id={f.id} hasFix={Boolean(f.fix_prompt)} />
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {runs.length > 0 ? (
        <Card className="p-4">
          <div className="type-subtext font-medium text-muted-foreground">Analysis history</div>
          <ul className="mt-2 space-y-1.5">
            {runs.map((r) => (
              <li key={r.id} className="flex items-center justify-between type-body">
                <span className="text-foreground">
                  {r.kind} <span className="text-muted-foreground">· {r.status}</span>
                </span>
                <span className="type-subtext text-muted-foreground">
                  {formatRelativeTime(r.started_at)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {!empty ? (
        <div className="flex justify-end">
          <RunAnalysisButton running={running} />
        </div>
      ) : null}
    </div>
  );
}

export const marketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/market",
  component: MarketPage,
});
